import  { useState, useContext, useEffect } from "react";
import { CustomButton, RegularButton } from "../../../common/ReusableComponent/CustomButton";
import ReusableModal from "../../AppPAges/ManageSettings/ReusableModal";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import { useToastHandler } from "../../../common/ToastHandler";
import AssetTable from "../AssetsInventory/AssetTable";
import { ReusableInput } from "../../../common/ReusableComponent//ReusableInput";
import AllocateIcon from "../../../../assets/Image/allocation.png"
import StatusPill from "../../../common/ReusableComponent/StatusPill";
import ActionButton from "../../../common/ReusableComponent/ActionButton";
import { FaUndo } from "react-icons/fa";
const AllocateAssets = () => {
  const { GetPaginatedData, Post, Patch } = useApi();
  const { handleToast } = useToastHandler();
  const { user } = useContext(AuthContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [allocationData, setAllocationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedAssetName, setSelectedAssetName] = useState("");
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [scannedBarcode, setScannedBarcode] = useState("");

  const [formData, setFormData] = useState({
    asset_id: "",
    allocated_to: "",
  });

  const [scannedAsset, setScannedAsset] = useState(null);


  const handleBarcodeScan = async (barcode) => {
    if (!barcode) return;

    try {
      setLoading(true);

      const res = await GetPaginatedData("asset", {
        search: barcode,
      });

      const asset = res?.data?.[0];

      if (!asset) {
        handleToast({
          type: "ERROR",
          message: "Asset not found for scanned barcode",
        });
        return;
      }
      setScannedAsset(asset);
      setFormData((prev) => ({
        ...prev,
        asset_id: asset.id,
      }));

      setSelectedAssetName(asset.name || asset.asset_name || "");

      handleToast({
        type: "SUCCESS",
        message: "Asset scanned successfully",
      });
    } catch {
      handleToast({
        type: "ERROR",
        message: "Failed to scan barcode",
      });
    } finally {
      setLoading(false);
      setScannedBarcode("");
    }
  };


  const fetchAllocationList = async ({ page = 1, searchTerm = "" } = {}) => {
    try {
      setLoading(true);

      const response = await GetPaginatedData("assetAllocation", {
        page,
        search: searchTerm || undefined,
      });

      const results = response?.data || [];
      setAllocationData(results);
      setTotalCount(response?.totalCount || 0);
      const totalCount = response?.totalCount || 0;

      return {
        data: results,
        totalPages: Math.ceil(totalCount / 10),
        totalCount,
        currentPage: page,
      };
    } catch {
      handleToast({
        type: "ERROR",
        message: "Failed to fetch asset allocations",
      });

      return {
        data: [],
        totalPages: 1,
        totalCount: 0,
        currentPage: page,
      };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchAllocationList({ page: currentPage, searchTerm: debouncedSearchTerm });
  }, [currentPage, debouncedSearchTerm, refreshTrigger]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.asset_id || !formData.allocated_to) {
      handleToast({
        type: "ERROR",
        message: "Please select both asset and employee",
      });
      return;
    }

    const payload = {
      asset_id: formData.asset_id,
      store: user.store,
      allocated_to: formData.allocated_to,
      allocated_by: user.user_id,
    };

    try {
      setLoading(true);
      await Post("assetAllocation", payload);

      handleToast({
        type: "SUCCESS",
        message: "Asset allocated successfully",
      });

      setFormData({ asset_id: "", allocated_to: "" });
      setSelectedAssetName("");
      setSelectedEmployeeName("");
      setShowModal(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      handleToast({
        type: "ERROR",
        message: "Failed to allocate asset",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleReturnAsset = async (row) => {
    if (!row?.id) return;

    try {
      setLoading(true);

      await Patch("assetAllocation", row.id, {
        status: "returned",
        return_date: new Date().toISOString().split("T")[0],
      });

      handleToast({
        type: "SUCCESS",
        message: "Asset returned successfully",
      });

      setRefreshTrigger(prev => prev + 1);
    } catch {
      handleToast({
        type: "ERROR",
        message: "Failed to return asset",
      });
    } finally {
      setLoading(false);
    }
  };

  const ALLOCATION_STATUS_PILL = {
    active: {
      bg: "#e9fdf3",
      text: "#12b76a",
      border: "#a6f4c5",
    },
    allocated: {
      bg: "#e9fdf3",
      text: "#12b76a",
      border: "#a6f4c5",
    },
    returned: {
      bg: "#fdecec",
      text: "#e11d48",
      border: "#fda4af",
    },
    pending: {
      bg: "#fff3cd",
      text: "#856404",
      border: "#ffeeba",
    },
  };

  const columns = [
    { header: "Asset", field: "asset_name" },
    { header: "Barcode", field: "asset_barcode" },
    { header: "Allocate By", field: "allocated_by_name" },
    { header: "Allocate To", field: "allocated_to_name" },
    { header: "Allocation Date", field: "date_of_allocation" },
    { header: "Return Date", field: "return_date" },
    {
      header: "Status",
      render: (row) => {
        const status = row.status || "active";
        const cfg =
          ALLOCATION_STATUS_PILL[status] || ALLOCATION_STATUS_PILL.active;

        return (
          <StatusPill
            label={status}
            bgColor={cfg.bg}
            textColor={cfg.text}
            borderColor={cfg.border}
          />
        );
      },
    },


    {
      header: "Action",
      render: (row) => {
        if (row.status === "active") {
          return (
            <ActionButton
              onClick={() => handleReturnAsset(row)}
              bgColor="#ecfeff"
              textColor="#0891b2"
              borderColor="#bae6fd"
              hoverBgColor="#cffafe"
            >
              <FaUndo />
              Return
            </ActionButton>
          );
        }
        return "-";
      },
    }

  ];

  return (
    <div className="responsive-box">
      <div className="inventory-header">
        <div className="inventory-title">Asset Allocation</div>
        <div
          className="btn-position"
          style={{ display: "flex", gap: "10px" }}
        >
          <CustomButton
            text="Allocate Asset"
            icon={AllocateIcon}
            onClick={() => setShowModal(true)}
            color="#000000"
            backgroundColor="#FFAF32"
          />
        </div>
      </div>

      <AssetTable
        data={allocationData}
        columns={columns}
        loading={loading}
        showFooter
        currentPage={currentPage}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        searchTerm={searchTerm}
        onSearch={handleSearch}
      />

      <ReusableModal
        title="Allocate Asset"
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          setFormData({ asset_id: "", allocated_to: "" });
          setSelectedAssetName("");
          setSelectedEmployeeName("");
          setScannedAsset(null);
          setScannedBarcode("");
        }}

        width="600px"
        height="400px"
      >
        <form onSubmit={handleSubmit}>
          <div
            className="p-3"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            <div style={{ flex: 1 }}>


              <div className="mb-3">
                <ReusableInput
                  type="text"
                  label="Scan Barcode"
                  value={scannedBarcode}
                  onChange={(e) => setScannedBarcode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Tab") {
                      e.preventDefault();
                      handleBarcodeScan(scannedBarcode.trim());
                    }
                  }}
                />
              </div>

              <div className="mb-3">
                <PaginatedSearchSelect
                  api="asset"
                  GetPaginatedData={GetPaginatedData}
                  getOptionLabel={(opt) => opt?.asset_name || opt?.name}
                  onSelect={(opt) => {
                    setFormData(prev => ({ ...prev, asset_id: opt?.id || "" }));
                    setSelectedAssetName(opt?.asset_name || opt?.name || "");
                    setScannedAsset(opt);
                  }}
                  value={
                    formData.asset_id
                      ? { id: formData.asset_id, asset_name: selectedAssetName }
                      : null
                  }
                  placeholder="Select Asset *"
                  required

                />
              </div>

              <div className="mb-3">
                <PaginatedSearchSelect
                  api="user"
                  GetPaginatedData={GetPaginatedData}
                  getOptionLabel={(opt) => opt?.name || opt?.first_name || opt?.username}
                  onSelect={(opt) => {
                    setFormData(prev => ({ ...prev, allocated_to: opt?.user_id || opt?.id || "" }));
                    setSelectedEmployeeName(opt?.name || opt?.first_name || opt?.username || "");
                  }}
                  value={
                    formData.allocated_to
                      ? { id: formData.allocated_to, name: selectedEmployeeName }
                      : null
                  }
                  placeholder="Select Employee *"
                  required
                />
              </div>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <RegularButton
                  text={loading ? "Allocating..." : "Allocate"}
                  type="submit"
                  backgroundColor="#282e26"
                  disabled={loading}
                />
              </div>
            </div>
            <div
              style={{
                background: "linear-gradient(135deg, #f9fafb, #ffffff)",
                borderRadius: "10px",
                padding: "16px",
                boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
                minHeight: "220px",
              }}
            >
              <h6
                style={{
                  marginBottom: "12px",
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                Asset Details
              </h6>

              {scannedAsset ? (
                <div style={{ fontSize: "14px", color: "#444" }}>
                  {[
                    ["Asset", scannedAsset.name],
                    ["Barcode", scannedAsset.barcode],
                    ["Category", scannedAsset.category_name],
                    ["Status", scannedAsset.status],
                    ["Warranty", scannedAsset.warranty],
                    [
                      "Warranty Period",
                      `${scannedAsset.warranty_start_date} → ${scannedAsset.warranty_end_date}`,
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "8px 0",
                        borderBottom: "1px dashed #ddd",
                      }}
                    >
                      <span style={{ fontWeight: 500, color: "#555" }}>
                        {label}
                      </span>
                      <span style={{ color: "#222" }}>{value || "-"}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#888",
                    fontSize: "14px",
                    border: "2px dashed #ddd",
                    borderRadius: "8px",
                  }}
                >
                  Scan barcode to preview asset details
                </div>
              )}
            </div>
          </div>
        </form>
      </ReusableModal>

      
    </div>
  );
};

export default AllocateAssets;